(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Revamp: the pinned story starts above tablet width; up to 1200px it stacks.
  const desktopMq = window.matchMedia('(min-width: 1201px)');

  // iOS Safari restores the saved scroll offset after load even when the visitor has
  // already started scrolling, yanking the page back to the top. Deferred, so this still
  // lands before load; back/forward come from the bfcache untouched.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  /** Keep a number between min and max. */
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /** Add `name` once `enter` crosses `threshold`; drop it when the slide has left. */
  function armMark(slide, name, enter, frac, threshold) {
    const on = slide.classList.contains(name);
    if (!on && frac <= LEAVING && enter >= threshold) slide.classList.add(name);
    else if (on && enter <= 0.001) slide.classList.remove(name);
  }

  /** Ceiling, in vw, on a layer's drift. Rates come off `data-hs-parallax`; images
   *  overhang their panel by 4vw, so this leaves margin against rounding. */
  const PARALLAX_ROOM = 3.6;

  /** How long a re-measure waits for the track to settle before giving up on it. */
  const REMEASURE_HOLD_MS = 120;

  /** How many of those waits it will sit through, so a long glide cannot starve it. */
  const REMEASURE_HOLDS = 12;

  /** How far a slide must come on screen before its timed content starts. Shared, so copy
   *  and imagery land together. */
  const IN_FRAME = 0.45;

  /** Campaign grid columns use VIDEO_IN (70% of the column, not the slide). */

  /** Carousel looks 2 and 3. Look 1 is the edge that uncovers the slide. */
  const LOOK_2_IN = 0.32;
  const LOOK_3_IN = 0.66;

  /** How far a slide may have started leaving and still arm. */
  const LEAVING = 0.4;

  /** Next video only takes over once this much of it is on screen. */
  const VIDEO_IN = 0.7;

  /** A mobile product row slides in once this much of it is on screen. */
  const ROW_IN = 0.8;

  /** Below this much of a beat left to cover, the track has arrived. */
  const SETTLED = 0.00002;

  /** Quiet needed after a glide before the section is re-measured. */
  const SETTLE_MS = 150;

  /** A wheel line, for the devices that report in lines, taken as a third of a notch. */
  const WHEEL_LINE = 33;

  /** Distance an arrow key asks for, matching canals' own 100. */
  const KEY_STEP = 100;

  /** Overlap left behind by a page key, so the line being read is not scrolled off. */
  const KEY_PAGE_GAP = 40;

  /** Drift from our own last write past which something else is taken to have scrolled. */
  const ADOPT_PX = 2;

  /** Distance left to close, in px, below which the page has arrived. */
  const SCROLL_SETTLED = 0.05;

  /** Pause between input events past which the next one is a new push, not momentum. */
  const GESTURE_GAP = 400;

  /** Longest a story's end may hold the page. Capped, or continuous input never gets out. */
  const HOLD_MS = 3000;

  /**
   * Page-wide smooth scrolling, modelled on canals-amsterdam: take the wheel, keep a target
   * offset, and each frame move the real offset a share of the remaining distance. That
   * share is the whole effect — a notch becomes a shove that decays as the gap closes.
   *
   * Canals translate one wide container; a theme has a header, footer and drawers that
   * expect a page that scrolls, so the eased value goes to the real offset instead. The
   * rest is guards: hand back what belongs elsewhere, stand down for popups, follow the
   * page when something else moves it.
   */
  const pageScroll = {
    stories: new Set(),
    target: 0,
    current: 0,
    applied: -1,
    pending: 0,
    max: 0,
    stamp: -1,
    moving: false,
    lerp: 0.1,
    gestureAt: -Infinity,
    holdAt: 0,
    holdId: null,
    holdSign: 0,
    holdUntil: 0,
    released: new Set(),

    /** Whether the driver is currently in charge of the page. */
    get driving() {
      return this.stories.size > 0;
    },

    /**
     * Start driving on behalf of a story; listeners go on with the first one.
     * @param {HorizontalStory} story Story asking for a smoothed page.
     * @returns {void}
     */
    add(story) {
      if (!this.driving) {
        window.addEventListener('wheel', this.onWheel, { passive: false });
        window.addEventListener('keydown', this.onKey, { passive: false });
        this.sync();
      }
      this.stories.add(story);
      this.lerp = story.lerp;
      this.measure();
    },

    /**
     * Stop driving for a story, and give the page back once none are left.
     * @param {HorizontalStory} story Story that no longer wants a smoothed page.
     * @returns {void}
     */
    remove(story) {
      this.stories.delete(story);
      if (this.driving) return;
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('keydown', this.onKey);
      this.moving = false;
      this.pending = 0;
      this.holdUntil = 0;
      this.holdId = null;
      this.released.clear();
    },

    /** Re-read how far the page can scroll. Forces layout, so only on height changes. */
    measure() {
      this.max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      this.target = clamp(this.target, 0, this.max);
    },

    /** Take the page's real offset as the truth, dropping anything in flight. */
    sync() {
      this.current = window.scrollY;
      this.target = this.current;
      this.applied = this.current;
      this.pending = 0;
      this.moving = false;
      this.holdUntil = 0;
      this.holdId = null;
      this.released.clear();
      this.markPassed(this.current);
    },

    /** Edges already behind this offset have been passed — don't pull back to them. */
    markPassed(at) {
      for (const story of this.stories) {
        for (const edge of story.holdEdges()) {
          if (at > edge.at + (story.stageHeight || 200)) this.released.add(edge.id);
        }
      }
    },

    /** Scrolling back before an edge means the next trip down should hold again. */
    unreleaseFrom(at) {
      for (const story of this.stories) {
        for (const edge of story.holdEdges()) {
          if (at < edge.at) this.released.delete(edge.id);
        }
      }
    },

    /** Keep a live hold on its (possibly remasured) edge. */
    reseatHold() {
      if (this.holdUntil <= performance.now()) {
        this.markPassed(this.current);
        return;
      }
      if (!this.holdId) return;
      for (const story of this.stories) {
        for (const edge of story.holdEdges()) {
          if (edge.id !== this.holdId) continue;
          this.holdAt = edge.at;
          this.target = clamp(edge.at, 0, this.max);
          return;
        }
      }
    },

    /**
     * The nearest story end a move would run into, if it runs into one.
     * @param {number} from Offset the page is headed to now.
     * @param {number} to Offset it would be headed to.
     * @returns {{id: string, at: number}|null} Edge to stop at, or null.
     */
    edgeBetween(from, to) {
      const down = to > from;
      let nearest = null;
      for (const story of this.stories) {
        for (const edge of story.holdEdges()) {
          if (edge.downOnly && !down) continue;
          if (down && this.released.has(edge.id)) continue;
          // Unreleased edges park even if measure put us on/past them — that is what
          // skipped the hold on load. Don't yank back from a later slide.
          const slack = story.stageHeight || 200;
          if (down ? edge.at > to || edge.at < from - slack : edge.at >= from || edge.at < to) continue;
          if (nearest === null || (down ? edge.at < nearest.at : edge.at > nearest.at)) {
            nearest = edge;
          }
        }
      }
      return nearest;
    },

    /**
     * The eased offset at full precision. The browser rounds what it is handed, and a
     * timeline read off a rounded offset steps in whole pixels.
     * @returns {number} Offset the page has eased to.
     */
    at() {
      return this.driving ? this.current : window.scrollY;
    },

    /**
     * Where the page is headed rather than where it got to, so sequences can arm on the
     * gesture instead of waiting out the glide.
     * @returns {number} Offset the page is easing towards.
     */
    aim() {
      return this.driving ? this.target : window.scrollY;
    },

    onWheel: (event) => pageScroll.wheel(event),
    onKey: (event) => pageScroll.input(event, keyPx(event), 'y'),

    /**
     * Take a wheel gesture on its dominant axis. Both are read: the story runs sideways, so
     * a swipe across it should move it on rather than trigger the browser's back gesture.
     * @param {WheelEvent} event Gesture to read.
     * @returns {void}
     */
    wheel(event) {
      const sideways = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      this.input(event, wheelPx(event, sideways), sideways ? 'x' : 'y');
    },

    /**
     * Take a gesture, unless it belongs to something else. Deltas are summed, not replaced:
     * keeping only the newest per frame throws away most of a gesture on a device that
     * reports faster than the display refreshes, and the page then feels like it resists.
     * @param {WheelEvent|KeyboardEvent} event Gesture to take.
     * @param {number} px Signed distance it asks for.
     * @param {'x'|'y'} axis Which way it was pointing.
     * @returns {void}
     */
    input(event, px, axis) {
      if (!this.driving || !px || event.ctrlKey || event.defaultPrevented) return;
      // A popup owns the screen while it is open, and its panel has to stay scrollable.
      for (const story of this.stories) if (story.locked) return;
      if (scrollableUnder(event.target, px, axis)) return;
      event.preventDefault();
      // A push after a real pause lifts any hold. Clocked here, past the guards, so one
      // handed off to a drawer doesn't count as a push.
      const now = performance.now();
      if (now - this.gestureAt > GESTURE_GAP) {
        if (this.holdId) this.released.add(this.holdId);
        this.holdUntil = 0;
      }
      this.gestureAt = now;
      this.pending += px;
      for (const story of this.stories) story.requestTick();
    },

    /**
     * Ease one frame's worth towards the target and write it to the page. Stamped, so
     * several stories sharing the driver move the page once a frame between them.
     * @param {number} time Timestamp of the frame being drawn.
     * @param {number} frames How many 60fps frames' worth of time this frame covers.
     * @returns {void}
     */
    step(time, frames) {
      if (!this.driving || this.stamp === time) return;
      this.stamp = time;

      // Something else moved the page — anchor link, scrollbar drag, the editor. Take its
      // word for where we are, carrying any gesture in flight across. `applied` moves with
      // it so following along isn't mistaken for a change worth writing, which would cancel
      // the theme's own smooth anchor jumps.
      if (Math.abs(window.scrollY - this.applied) > ADOPT_PX) {
        const carry = this.target - this.current;
        this.current = window.scrollY;
        this.applied = window.scrollY;
        if (time < this.holdUntil) {
          this.reseatHold();
        } else {
          this.target = clamp(this.current + carry, 0, this.max);
          this.holdUntil = 0;
          this.holdId = null;
          this.markPassed(this.current);
        }
      }

      if (this.pending) {
        const delta = this.pending;
        this.pending = 0;
        if (delta < 0) {
          this.unreleaseFrom(clamp(this.target + delta, 0, this.max));
          this.holdUntil = 0;
          this.holdId = null;
        }
        if (time < this.holdUntil && delta * this.holdSign > 0) {
          // Held at an end and still pushing the same way: momentum, so it is dropped.
          // Reversing, pausing, or the cap running out all let it go.
          this.target = this.holdAt;
        } else {
          if (this.holdId && time >= this.holdUntil) this.released.add(this.holdId);
          const to = clamp(this.target + delta, 0, this.max);
          const edge = this.edgeBetween(this.target, to);
          if (edge === null) {
            this.target = to;
            this.holdUntil = 0;
            this.holdId = null;
          } else {
            this.target = edge.at;
            this.holdAt = edge.at;
            this.holdId = edge.id;
            this.holdSign = delta > 0 ? 1 : -1;
            this.holdUntil = time + HOLD_MS;
          }
        }
      }

      const gap = this.target - this.current;
      if (Math.abs(gap) < SCROLL_SETTLED) {
        this.current = this.target;
        this.moving = false;
      } else {
        // Frame-rate independent: at 60fps this closes `lerp` of the gap, and at any other
        // rate the share that lands in the same place at the same moment in time.
        this.current += gap * (1 - (1 - this.lerp) ** frames);
        this.moving = true;
      }

      const next = Math.round(this.current * 100) / 100;
      if (next === this.applied) return;
      this.applied = next;
      // `instant` matters: the theme sets `scroll-behavior: smooth` on `html`, so the plain
      // form would have the browser ease towards each of these sixty times a second, every
      // one abandoning the last. Two easings stacked never arrive.
      window.scrollTo({ top: next, left: 0, behavior: 'instant' });
    },
  };

  /**
   * Distance a wheel gesture asks for, in pixels. Passed through at whatever the OS
   * reported: canals scale theirs to two thirds, which on a storefront reads as a page that
   * is hard to push. Pace belongs to the story's setting, not to a tax on input.
   * @param {WheelEvent} event Gesture to read.
   * @param {boolean} sideways Whether to read the horizontal delta rather than the vertical.
   * @returns {number} Signed distance in pixels.
   */
  function wheelPx(event, sideways) {
    const delta = sideways ? event.deltaX : event.deltaY;
    if (event.deltaMode === 1) return delta * WHEEL_LINE;
    // Page-mode deltas count viewports, so it has to be the one being moved along.
    if (event.deltaMode === 2) return delta * (sideways ? window.innerWidth : window.innerHeight);
    return delta;
  }

  /**
   * Distance a key press asks for. The wheel is being taken, so these must be answered too,
   * or the page would move differently depending on how it was asked.
   * @param {KeyboardEvent} event Key press to read.
   * @returns {number} Signed distance, or 0 for keys that are none of this code's business.
   */
  function keyPx(event) {
    if (event.metaKey || event.altKey || event.ctrlKey) return 0;
    const el = event.target;
    // Typing, and the keys that operate a control, belong to whatever has focus.
    if (el instanceof Element) {
      if (el.isContentEditable || el.closest('input, textarea, select, [contenteditable]')) return 0;
      if (event.key === ' ' && el.closest('a, button, summary, [role="button"]')) return 0;
    }
    const page = window.innerHeight - KEY_PAGE_GAP;
    if (event.key === 'ArrowDown') return KEY_STEP;
    if (event.key === 'ArrowUp') return -KEY_STEP;
    if (event.key === 'PageDown') return page;
    if (event.key === 'PageUp') return -page;
    if (event.key === ' ') return event.shiftKey ? -page : page;
    return 0;
  }

  /**
   * Whether something between the target and the page can still scroll the way the gesture
   * is going, on the axis it is going. Smoothing the page under someone scrolling a drawer
   * moves the wrong thing. The axis is part of the question because a component that
   * scrolls one way usually cannot scroll the other.
   * @param {EventTarget} target Element the gesture landed on.
   * @param {number} px Signed distance the gesture asks for.
   * @param {'x'|'y'} axis Which way the gesture was pointing.
   * @returns {boolean} True when the gesture belongs to something else.
   */
  function scrollableUnder(target, px, axis) {
    const sideways = axis === 'x';
    let el = target instanceof Element ? target : null;
    // The stage holds nothing scrollable and is what the wheel is over for most of the
    // page. Worth skipping: the walk reads scroll sizes, so it forces layout mid-gesture.
    if (el && el.closest('[data-hs-sticky]')) return false;
    while (el && el !== document.body && el !== document.documentElement) {
      const span = sideways
        ? el.scrollWidth - el.clientWidth
        : el.scrollHeight - el.clientHeight;
      if (span > 1) {
        const style = window.getComputedStyle(el);
        const overflow = sideways ? style.overflowX : style.overflowY;
        if (/(auto|scroll)/.test(overflow)) {
          const at = sideways ? el.scrollLeft : el.scrollTop;
          const room = px > 0 ? span - at : at;
          if (room > 1) return true;
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  /** Ease both ends of a beat, so motion and fades neither start nor stop abruptly. */
  const smooth = (value, span = 1) => {
    const p = clamp(value / span, 0, 1);
    return p * p * p * (p * (p * 6 - 15) + 10);
  };

  /**
   * Write a custom property only when the painted value changes. Each write invalidates the
   * slide's styles, and most slides sit off-frame at the same value every frame.
   * @param {HTMLElement} el Element to write the property to.
   * @param {string} name Custom property name, including the leading `--`.
   * @param {number} value Value to write, rounded to the four decimals that can paint.
   * @returns {void}
   */
  function setVar(el, name, value) {
    const next = value.toFixed(4);
    if (!el._hsVars) el._hsVars = {};
    if (el._hsVars[name] === next) return;
    el._hsVars[name] = next;
    el.style.setProperty(name, next);
  }

  /** Return the breakpoint-correct video inside a slide or panel (desktop vs mobile). */
  function visibleVideo(scope) {
    if (!scope) return null;
    const videos = scope.querySelectorAll('video');
    if (!videos.length) return null;
    const isDesktop = desktopMq.matches;
    let match = null;
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      if (isDesktop && video.classList.contains('hs-video--mobile')) continue;
      if (!isDesktop && video.classList.contains('hs-video--desktop')) continue;
      match = video;
    }
    return match || videos[0];
  }

  class HorizontalStory {
    /** Wire up one horizontal-story section and run the first paint. */
    constructor(root) {
      this.root = root;
      this.sticky = root.querySelector('[data-hs-sticky]');
      this.track = root.querySelector('[data-hs-track]');
      this.slides = Array.from(root.querySelectorAll('[data-hs-slide]'));
      this.campaign = this.slides.find((slide) => slide.dataset.hsType === 'campaign') || null;
      this.dwell = parseFloat(root.dataset.dwell || '0.6');
      this.soundOn = root.dataset.soundDefault === 'true';
      this.activeIndex = 0;
      this.videoIndex = 0;
      this.videoLook = -1;
      this.headerHidden = false;
      this.gridWarmed = false;
      this.timeline = 0;
      this.rendered = 0;
      this.targetPos = 0;
      this.sectionHeight = 0;
      this.raf = null;
      this.lastFrame = 0;
      this.frames = 1;
      this.locked = false;
      this.lockObserver = null;
      this.stageInset = 0;
      this.stageHeight = window.innerHeight;
      this.stickyHeight = window.innerHeight;
      this.sectionTop = 0;
      this.trackShift = null;
      this.settleTimer = 0;
      this.pinned = false;
      this.lastScrollY = -1;
      this.moving = false;
      this.header = null;
      this.headerOverlap = null;
      this.desktop = desktopMq.matches;
      this.vertical = !this.desktop;
      this.slideObserver = null;
      this.carouselTimer = null;
      this.carouselSlide = null;
      this.carouselScrolling = false;
      this.carouselIdleTimer = 0;
      const section = root.closest('.shopify-section');
      this.leadSection = !!section && !section.previousElementSibling;
      // 1 (direct) … 10 (very glidy) from the editor, as the fraction of the remaining
      // distance covered each frame. Geometric, so each step coasts noticeably longer than
      // the last, and scaled so the default of 7 lands on canals' 0.1. With page smoothing
      // on this eases the page; with it off, the track alone — where the same number reads
      // as a longer wait, so around 4 suits that mode better.
      const smoothing = clamp(parseFloat(root.dataset.smoothing || '7') || 7, 1, 10);
      this.lerp = clamp(0.35 * Math.pow(0.81, smoothing - 1), 0.04, 0.35);
      // Reduced motion gets the browser's own scrolling, untouched.
      this.pageSmoothing = root.dataset.pageSmoothing !== 'false' && !reduceMotion;
      this.tick = this.tick.bind(this);
      this.requestTick = this.requestTick.bind(this);
      this.refresh = this.refresh.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.prepareVideos();
      this.recalcTimeline();
      this.measureStage();
      this.setPinHeight();
      this.bind();
      if (this.desktop) {
        const carousel = this.slides.find((slide) => slide.dataset.hsType === 'carousel');
        if (carousel) this.warmCarouselLooks(carousel);
      }
      this.update();
      // A campaign block without a video has nothing to wait for before showing the grid.
      if (this.campaign && (!visibleVideo(this.campaign) || reduceMotion)) this.revealCampaignGrid();
    }

    /** Attach resize, click, and breakpoint listeners, then the per-mode ones. */
    bind() {
      window.addEventListener('resize', this.refresh, { passive: true });
      // Webfonts are requested after this script, so they can still swap in after the stage
      // is measured — and a swap moves the text header the section pins against.
      if (document.fonts) document.fonts.ready.then(this.refresh);
      this.root.addEventListener('click', this.onClick);
      this.watchScrollLock();
      if (desktopMq.addEventListener) desktopMq.addEventListener('change', this.onBreakpoint);
      else desktopMq.addListener(this.onBreakpoint);
      window.addEventListener('load', this.refresh);
      this.bindPlayRetries();
      this.bindMode();
    }

    /**
     * Safari drops autoplay when the file is not ready yet, the tab was backgrounded, or
     * Low Power Mode blocked the first play(). Retry once the page can actually start it.
     */
    bindPlayRetries() {
      this.onResumePlay = () => {
        if (document.visibilityState === 'hidden') return;
        this.syncVideos();
      };
      this.onFirstGesture = () => {
        document.removeEventListener('touchstart', this.onFirstGesture, true);
        document.removeEventListener('pointerdown', this.onFirstGesture, true);
        const carousel = this.slides.find((slide) => slide.dataset.hsType === 'carousel');
        if (carousel) this.warmCarouselLooks(carousel);
        this.syncVideos();
      };
      document.addEventListener('visibilitychange', this.onResumePlay);
      window.addEventListener('pageshow', this.onResumePlay);
      document.addEventListener('touchstart', this.onFirstGesture, { capture: true, passive: true });
      document.addEventListener('pointerdown', this.onFirstGesture, { capture: true, passive: true });
    }

    /** Desktop scrubs a pinned track; mobile is a normal vertical page. */
    bindMode() {
      window.removeEventListener('scroll', this.requestTick);
      this.stopCarouselAuto();
      window.clearTimeout(this.carouselIdleTimer);
      this.carouselScrolling = false;
      // Which video in a look is live is a breakpoint question, so the cached answers stop
      // being true here.
      for (const slide of this.slides) slide.hsVideos = null;
      pageScroll.remove(this);
      // The loop is about to stop or restart, so anything waiting on motion to finish would
      // wait on a frame that never comes.
      this.moving = false;
      this.lastScrollY = -1;
      // Stacked slides have no sticky stage, so a hold left from a popup open across the
      // breakpoint would pin the whole story to the screen.
      this.root.classList.remove('hs--hold');
      if (this.slideObserver) {
        this.slideObserver.disconnect();
        this.slideObserver = null;
      }
      if (this.onVerticalScroll) {
        window.removeEventListener('scroll', this.onVerticalScroll);
        this.onVerticalScroll = null;
      }
      if (this.vertical) {
        this.clearParallax();
        this.bindVertical();
        return;
      }
      this.root.classList.remove('hs--enter');
      window.addEventListener('scroll', this.requestTick, { passive: true });
      // Phones scroll their own way, and well; this is only ever about the wheel.
      if (this.pageSmoothing) pageScroll.add(this);
    }

    /** The last position on the timeline. */
    maxPos() {
      return Math.max(this.timeline, 0);
    }

    /** Play whichever slide is on screen; there is no pinned timeline on mobile. */
    bindVertical() {
      const reveal = (slide) => {
        slide.querySelectorAll('[data-hs-enter]').forEach((el) => {
          if (el.classList.contains('hs-carousel__brand') || el.classList.contains('hs-carousel__tagline') || el.classList.contains('hs-products__cell') || el.classList.contains('hs-statement__line')) return;
          el.classList.add('is-in');
        });
      };
      this.slides.forEach((slide) => {
        const box = slide.getBoundingClientRect();
        if (box.bottom > 0 && box.top < window.innerHeight) reveal(slide);
      });
      this.root.classList.add('hs--enter');
      this.slideObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) reveal(entry.target);
        });
        this.syncVerticalActive();
      }, { threshold: [0, 0.15, 0.35, 0.7, 1] });
      this.slides.forEach((slide) => this.slideObserver.observe(slide));
      this.onVerticalScroll = () => {
        this.markCarouselScrolling();
        if (this.verticalScrollTick) return;
        this.verticalScrollTick = true;
        requestAnimationFrame(() => {
          this.verticalScrollTick = false;
          if (this.vertical) this.syncVerticalActive();
        });
      };
      window.addEventListener('scroll', this.onVerticalScroll, { passive: true });
      this.syncVerticalActive();
    }

    /** Mark the most visible slide active so its video can play. */
    syncVerticalActive() {
      let best = 0;
      let bestScore = -1;
      let live = 0;
      this.slides.forEach((slide, index) => {
        const box = slide.getBoundingClientRect();
        const visible = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
        const score = Math.max(visible, 0);
        slide.classList.toggle('is-active', score > box.height * 0.25);
        if (score > bestScore) {
          bestScore = score;
          best = index;
        }
        if (this.boxVisible(slide) >= VIDEO_IN) live = index;
      });
      if (bestScore > 0 && this.boxVisible(this.slides[live]) < VIDEO_IN) live = best;
      this.activeIndex = best;
      this.videoIndex = live;
      this.videoLook = 0;
      this.revealCarouselLockup();
      this.revealCampaignRows();
      this.revealStatementLines();
      this.driveMobileCarousel();
      this.syncVideos();
      this.syncCarouselAuto();
    }

    /** ICONS wordmark waits until the slide is 70% on; earlier and the fade is already over. */
    revealCarouselLockup() {
      this.slides.forEach((slide) => {
        if (slide.dataset.hsType !== 'carousel') return;
        const stage = slide.querySelector('.hs-carousel__stage') || slide;
        if (this.boxVisible(stage) < VIDEO_IN) return;
        const brand = slide.querySelector('.hs-carousel__brand');
        const tag = slide.querySelector('.hs-carousel__tagline');
        if (brand) brand.classList.add('is-in');
        if (tag) tag.classList.add('is-in');
      });
    }

    /** Mobile product rows slide in once that row is 80% on screen. */
    revealCampaignRows() {
      if (!this.vertical || !this.campaign) return;
      const cells = this.campaign.querySelectorAll('.hs-campaign-grid__track .hs-products__cell');
      for (let row = 0; row < 3; row++) {
        const left = cells[row * 2];
        const right = cells[row * 2 + 1];
        if (!left || this.rowVisible(left, right) < ROW_IN) continue;
        left.classList.add('is-in');
        if (right) right.classList.add('is-in');
      }
    }

    /** 0–1 of a two-cell row that sits in the viewport. */
    rowVisible(a, b) {
      if (!a) return 0;
      const r1 = a.getBoundingClientRect();
      const r2 = b ? b.getBoundingClientRect() : r1;
      const left = Math.min(r1.left, r2.left);
      const right = Math.max(r1.right, r2.right);
      const top = Math.min(r1.top, r2.top);
      const bottom = Math.max(r1.bottom, r2.bottom);
      const area = (right - left) * (bottom - top);
      if (area <= 0) return 0;
      const visW = Math.min(right, window.innerWidth) - Math.max(left, 0);
      const visH = Math.min(bottom, window.innerHeight) - Math.max(top, 0);
      if (visW <= 0 || visH <= 0) return 0;
      return clamp((visW * visH) / area, 0, 1);
    }

    /**
     * Mobile statement copy is scrubbed by scroll while the image is pinned, as ICONS is:
     * nothing moves until the pin reaches the top, then the first 80% of the held scroll
     * draws the left line in, the right a beat behind, and the rest is a pause on the
     * finished frame. Scrolling back runs it in reverse.
     */
    revealStatementLines() {
      if (!this.vertical) return;
      this.slides.forEach((slide) => {
        if (slide.dataset.hsType !== 'statement') return;
        const box = slide.getBoundingClientRect();
        const range = Math.max(box.height - window.innerHeight, 1);
        const p = reduceMotion ? 1 : clamp(-box.top / (range * 0.8), 0, 1);
        slide.querySelectorAll('.hs-statement__line').forEach((line) => {
          const right = line.classList.contains('hs-statement__line--right');
          const local = clamp(right ? (p - 0.3) / 0.7 : p / 0.7, 0, 1);
          line.style.setProperty('--hs-line-p', local.toFixed(4));
        });
      });
    }

    /** Look 0–2 from how far the pinned ICONS block has been scrolled. */
    carouselScrollLook(slide) {
      const box = slide.getBoundingClientRect();
      const total = slide.querySelectorAll('[data-hs-carousel-item]').length || 3;
      const range = Math.max(box.height - window.innerHeight, 1);
      const t = clamp(-box.top / range, 0, 0.999);
      return Math.min(total - 1, Math.floor(t * total));
    }

    /** Scroll drives the look. A tap locks it; scrolling to another look unlocks. */
    driveMobileCarousel() {
      if (!this.vertical) return;
      const slide = this.slides.find((item) => item.dataset.hsType === 'carousel');
      if (!slide || this.boxVisible(slide.querySelector('.hs-carousel__pin') || slide) < 0.2) return;
      const look = this.carouselScrollLook(slide);
      if (slide.dataset.hsCarouselLocked) {
        const current = Number(slide.dataset.hsCarouselIndex || 0);
        if (!this.carouselScrolling || look === current) return;
        delete slide.dataset.hsCarouselLocked;
      }
      if (this.carouselScrolling) this.setCarouselIndex(slide, look, false);
    }

    /** Mobile: the live ICONS look is a playing video that has not reached its end yet. */
    carouselHeld(slide) {
      if (!this.vertical) return false;
      const video = visibleVideo(slide.querySelector('[data-hs-carousel-item].is-active'));
      return !!video && !video.hsLookDone && !video.paused && video.readyState >= 2;
    }

    /** Autoplay held on a video look until it played through; move it on now. */
    onCarouselLookDone(video) {
      if (!this.vertical) return;
      const slide = video.closest('[data-hs-slide]');
      const panel = video.closest('[data-hs-carousel-item]');
      if (!slide || !panel || !panel.classList.contains('is-active') || slide.dataset.hsCarouselLocked) return;
      if (!this.carouselTimer || this.carouselSlide !== slide) return;
      const current = Number(slide.dataset.hsCarouselIndex || 0);
      const total = slide.querySelectorAll('[data-hs-carousel-item]').length;
      this.setCarouselIndex(slide, (current + 1) % total, false);
      // A fresh interval, so the next look gets its full turn.
      this.stopCarouselAuto();
      this.syncCarouselAuto();
    }

    /** While the finger is moving, pause autoplay; when it settles, autoplay resumes. */
    markCarouselScrolling() {
      this.carouselScrolling = true;
      this.stopCarouselAuto();
      window.clearTimeout(this.carouselIdleTimer);
      this.carouselIdleTimer = window.setTimeout(() => {
        this.carouselScrolling = false;
        this.syncCarouselAuto();
      }, 600);
    }

    /** Cycle looks while ICONS is on screen and the page is still. A tap locks it. */
    syncCarouselAuto() {
      const slide = this.slides.find((item) => item.dataset.hsType === 'carousel');
      if (
        this.vertical
        && !reduceMotion
        && !this.carouselScrolling
        && slide
        && this.boxVisible(slide.querySelector('.hs-carousel__pin') || slide) >= 0.35
        && !slide.dataset.hsCarouselLocked
      ) {
        this.startCarouselAuto(slide);
        return;
      }
      this.stopCarouselAuto();
    }

    startCarouselAuto(slide) {
      if (this.carouselTimer && this.carouselSlide === slide) return;
      this.stopCarouselAuto();
      this.carouselSlide = slide;
      this.carouselTimer = window.setInterval(() => {
        const total = slide.querySelectorAll('[data-hs-carousel-item]').length;
        if (total < 2 || this.carouselHeld(slide)) return;
        const current = Number(slide.dataset.hsCarouselIndex || 0);
        this.setCarouselIndex(slide, (current + 1) % total, false);
      }, 3200);
    }

    stopCarouselAuto() {
      if (this.carouselTimer) window.clearInterval(this.carouselTimer);
      this.carouselTimer = null;
      this.carouselSlide = null;
    }

    /** Rebuild listeners when the viewport crosses the desktop breakpoint. */
    onBreakpoint() {
      this.refresh();
    }

    /** Tear down listeners, pause videos, and restore the site header. */
    destroy() {
      window.removeEventListener('scroll', this.requestTick);
      window.removeEventListener('resize', this.refresh);
      pageScroll.remove(this);
      this.root.removeEventListener('click', this.onClick);
      if (desktopMq.removeEventListener) desktopMq.removeEventListener('change', this.onBreakpoint);
      else if (desktopMq.removeListener) desktopMq.removeListener(this.onBreakpoint);
      window.clearTimeout(this.settleTimer);
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.lockObserver) this.lockObserver.disconnect();
      this.stopCarouselAuto();
      document.removeEventListener('visibilitychange', this.onResumePlay);
      window.removeEventListener('pageshow', this.onResumePlay);
      document.removeEventListener('touchstart', this.onFirstGesture, true);
      document.removeEventListener('pointerdown', this.onFirstGesture, true);
      this.root.querySelectorAll('video').forEach((video) => video.pause());
      this.root.classList.remove('hs--hold');
      this.setHeaderHidden(false);
    }

    /** Configure playsinline/mute/loop and the poster hand-off for every video. */
    prepareVideos() {
      this.root.querySelectorAll('video').forEach((video) => {
        const once = !!video.closest('.hs-media--once');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.playsInline = true;
        // Safari keys autoplay off the muted attribute / defaultMuted, not only `.muted`.
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        // ICONS 2 and 3 stay off-screen; metadata-only means they have not fetched
        // when the handoff asks them to play. Auto lets the file start on load.
        const carouselPanel = video.closest('[data-hs-carousel-item]');
        video.preload = carouselPanel ? 'auto' : 'metadata';
        video.loop = !once;
        // It loops, so `ended` never fires: the end is the clock reaching the last beat
        // or jumping back to the start.
        if (carouselPanel) {
          video.addEventListener('timeupdate', () => {
            const t = video.currentTime;
            const wrapped = t + 0.5 < (video.hsLastT || 0);
            video.hsLastT = t;
            if (video.hsLookDone || !(wrapped || (video.duration && t >= video.duration - 0.3))) return;
            video.hsLookDone = true;
            this.onCarouselLookDone(video);
          });
        }
        // Only the campaign video plays once. If it never loads, nothing would uncover the
        // image grid behind it.
        if (once) video.addEventListener('error', () => this.revealCampaignGrid(), { once: true });
        this.bindPosterHandoff(video);
      });
    }

    /**
     * Fade a video in only once it can paint a frame. Safari drops its own poster as soon
     * as playback is requested but paints nothing for a beat, which read as a white blink.
     */
    bindPosterHandoff(video) {
      if (!video.closest('.hs-media--has-poster') || video.readyState >= 2) return;
      // Our own poster layer replaces the native one, so Safari has no poster to drop.
      video.removeAttribute('poster');
      video.classList.add('is-pending');
      const reveal = () => video.classList.remove('is-pending');
      ['loadeddata', 'playing', 'error'].forEach((type) => {
        video.addEventListener(type, reveal, { once: true });
      });
    }

    /**
     * Start the grid images downloading a slide early. They wait off-frame behind a
     * transform, so a lazy one can still be unloaded when the panel slides in.
     */
    warmGridImages() {
      if (this.gridWarmed || !this.campaign) return;
      this.gridWarmed = true;
      this.campaign.querySelectorAll('.hs-campaign-grid img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    /**
     * 0–1 of a product column's home slot on screen. Cells sit translated off-frame
     * until they arm, so measuring the cell itself never reached 70%.
     */
    gridColumnVisible(slide, index) {
      const track = slide && slide.querySelector('.hs-campaign-grid__track');
      if (!track) return 0;
      const box = track.getBoundingClientRect();
      const colW = box.width / 3;
      if (colW <= 0) return 0;
      const left = box.left + colW * index;
      const visW = Math.min(left + colW, window.innerWidth) - Math.max(left, 0);
      if (visW <= 0) return 0;
      return clamp(visW / colW, 0, 1);
    }

    /** Slide a grid column in once 70% of that column's slot is on screen. */
    armGridColumn(slide, name, index, frac, enter) {
      const on = slide.classList.contains(name);
      if (!on && frac <= LEAVING && this.gridColumnVisible(slide, index) >= VIDEO_IN) {
        slide.classList.add(name);
      } else if (on && enter <= 0.001) {
        slide.classList.remove(name);
      }
    }

    /** Arm the campaign grid: the columns cascade in from this class. */
    revealCampaignGrid() {
      if (!this.campaign || this.campaign.classList.contains('is-grid-ready')) return;
      this.campaign.classList.add('is-grid-ready');
      if (reduceMotion) this.campaign.classList.add('is-grid-col-2', 'is-grid-col-3');
      this.syncVideos();
    }

    /** Hide the grid and restart the campaign video (used when scrolling back). */
    replayCampaignVideo(slide) {
      const video = visibleVideo(slide);
      if (!video) return;
      slide.classList.remove('is-grid-ready', 'is-grid-col-2', 'is-grid-col-3');
      video.currentTime = 0;
      this.playVideo(slide, video);
    }

    /**
     * Cache the timeline length and where each slide sits on it. One beat to a slide, so the
     * beat is the slide's index and the track's X too — see `render`. `drift` is how far
     * either side of centre parallax is measured across: one beat, which is how long a slide
     * is on screen.
     */
    recalcTimeline() {
      const last = this.slides.length - 1;
      this.spans = this.slides.map((slide, i) => ({ centre: i, drift: 1 }));
      this.timeline = Math.max(last, 0);
    }

    /**
     * Apps inject bars above the story after load (the free shipping bar), pushing the
     * section down and the bottom of the stage off screen. Shrink the stage by whatever
     * shows above it rather than pulling the section up over it, so the bar keeps its space
     * and the stage grows back as it scrolls away.
     */
    measureStage() {
      this.measureGeometry();
      this.syncStageInset();
    }

    /**
     * Cache the boxes the stage maths run on. Every read forces layout, so this runs on
     * resize, on injected bars, and once at the top of an interaction — never per frame.
     */
    measureGeometry() {
      this.sectionTop = this.root.getBoundingClientRect().top + window.scrollY;
      // Mobile hero fills the screen below whatever sits above it (header, app bars).
      // Page offset, not live inset, so the video doesn't resize while scrolling.
      // Capped at half the screen, as the stage inset is, so a tall stack above can't
      // collapse the hero.
      const heroTop = this.leadSection
        ? clamp(Math.round(this.sectionTop), 0, Math.round(window.innerHeight / 2))
        : 0;
      if (heroTop !== this.heroTop) {
        this.heroTop = heroTop;
        this.root.style.setProperty('--hs-hero-top', `${heroTop}px`);
      }
      this.stickyHeight = this.sticky.offsetHeight;
      this.measureHeaderOverlap();
    }

    /**
     * Trim the stage by however much of the section is still below the top of the screen.
     * Runs every frame, but off the cached section offset, so it costs no layout.
     */
    syncStageInset(scrollY) {
      const offset = typeof scrollY === 'number' ? scrollY : window.scrollY;
      // Only the lead section starts at the top of the page; elsewhere the stage pins to a
      // clear viewport and whatever is above has already scrolled past.
      const top = this.leadSection ? this.sectionTop - offset : 0;
      const inset = clamp(Math.round(top), 0, Math.round(window.innerHeight / 2));
      if (inset !== this.stageInset) {
        this.stageInset = inset;
        this.root.style.setProperty('--hs-stage-inset', `${inset}px`);
      }
      // Full-screen height whatever the stage is trimmed to, so the timeline doesn't shift
      // while the bar scrolls out.
      this.stageHeight = this.stickyHeight + inset;
    }

    /**
     * How far the header reaches into the stage, which is where the sidebar must start.
     * --header-height can't answer it: that measures the whole header block, while the stage
     * may already begin below it.
     */
    measureHeaderOverlap() {
      if (!this.header || !this.header.isConnected) {
        this.header = document.querySelector('[data-header-height]');
        if (!this.header) return;
      }
      const box = this.header.getBoundingClientRect();
      const overlap = clamp(box.bottom - this.sticky.getBoundingClientRect().top, 0, box.height);
      const value = Math.round(overlap);
      // Only on change: writing a custom property invalidates styles for the whole stage.
      if (value === this.headerOverlap) return;
      this.headerOverlap = value;
      this.root.style.setProperty('--hs-header-overlap', `${value}px`);
    }

    /**
     * Size the section so there is scroll to spend on the story. The stage sticks for the
     * whole of it and the offset within it is what the timeline reads, so the horizontal
     * movement *is* the vertical scroll rather than something competing with it.
     */
    setPinHeight() {
      if (this.vertical) {
        this.root.style.height = '';
        this.sectionHeight = 0;
        return;
      }
      // Kept rather than read back, so progress() never has to measure the section.
      this.sectionHeight = this.stageHeight * (1 + this.maxPos() * this.dwell);
      this.root.style.height = `${this.sectionHeight}px`;
      // The document just got taller or shorter, and the driver clamps against its length.
      pageScroll.measure();
      pageScroll.reseatHold();
    }

    /**
     * Offset where the timeline runs out, last slide squared up. The driver stops a push
     * here so the story has an end to land on. This end only: holding at the start too made
     * arriving at the story its own push.
     * @returns {number|null} Offset, or null with no pinned run.
     */
    exitEdge() {
      const range = this.sectionHeight - this.stageHeight;
      if (this.vertical || range <= 0) return null;
      return this.sectionTop + range;
    }

    /** ICONS fully on — same park as the last slide, before campaign starts to enter. */
    iconsHoldEdge() {
      const range = this.sectionHeight - this.stageHeight;
      const last = this.maxPos();
      const index = this.slides.findIndex((slide) => slide.dataset.hsType === 'carousel');
      if (this.vertical || range <= 0 || last <= 0 || index < 0) return null;
      return this.sectionTop + range * (index / last);
    }

    /** Parks last slide and ICONS going forward only — no hold scrolling back. */
    holdEdges() {
      const edges = [];
      const exit = this.exitEdge();
      if (exit !== null) edges.push({ id: 'exit', at: exit, downOnly: true });
      const icons = this.iconsHoldEdge();
      if (icons !== null) edges.push({ id: 'icons', at: icons, downOnly: true });
      return edges;
    }

    /** 0–1 how far the visitor has scrolled through the pinned section. */
    progress(scrollY) {
      const range = this.sectionHeight - this.stageHeight;
      if (range <= 0) return 0;
      const offset = typeof scrollY === 'number' ? scrollY : window.scrollY;
      return clamp((offset - this.sectionTop) / range, 0, 1);
    }

    /** Queue one smoothing frame (also the scroll handler). */
    requestTick() {
      if (this.vertical || this.raf) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    /**
     * One frame: read where the page is, then ease the track towards the position that
     * offset asks for. With page smoothing off the glide lives in the track rather than the
     * page, which leaves scrolling to the browser — native momentum, every input device,
     * and no scroll event fired per frame for the theme's own listeners to run on.
     */
    tick(now) {
      this.raf = null;
      if (this.vertical) return;
      // A popup owns the screen while it is open; hold the last frame until it closes.
      if (this.locked) {
        this.lastFrame = 0;
        return;
      }
      const time = typeof now === 'number' ? now : performance.now();
      this.frames = clamp((time - (this.lastFrame || time)) / 16.67, 0.5, 3);
      this.lastFrame = time;

      // Move the page first, then paint from the offset just written, so the track and the
      // page can never end up a frame apart.
      pageScroll.step(time, this.frames);

      const scrollY = pageScroll.at();
      this.targetPos = this.targetPosition(pageScroll.aim());
      // Kept every frame because a popup can only be answered from the last known good
      // state — by the time one is detected it has already moved the page.
      this.pinned = scrollY > this.sectionTop
        && scrollY < this.sectionTop + this.sectionHeight - this.stageHeight;

      if (pageScroll.driving) {
        // One source of easing. The page is already gliding, so damping again here would
        // stack two coasts and read as the slides lagging the scroll.
        this.rendered = this.targetPosition(scrollY);
      } else {
        // Native page scroll, so the glide lives here. Frame-rate independent: at 60fps it
        // closes `lerp` of the gap, at any other rate the share landing in the same place.
        const gap = this.targetPos - this.rendered;
        if (Math.abs(gap) <= SETTLED) {
          this.rendered = this.targetPos;
        } else {
          this.rendered += gap * (1 - (1 - this.lerp) ** this.frames);
        }
      }

      // Painting a section nowhere near the screen is the expensive half of the frame and
      // buys nothing, so scrolling past the story costs what a page without one would.
      const visible = this.inView(scrollY);
      if (visible) {
        this.syncStageInset(scrollY);
        this.render(this.rendered);
      }

      const moving = pageScroll.moving
        || this.rendered !== this.targetPos
        || scrollY !== this.lastScrollY;
      this.lastScrollY = scrollY;
      if (moving !== this.moving) {
        this.moving = moving;
        if (!moving) this.scheduleMeasure();
      }

      // Sample every frame while the story is on screen, not only on frames a scroll event
      // lands on. Events are coalesced and can arrive just after the frame that needed
      // them, which fed the damping a target that jumped and stalled. Idling is near free:
      // every write below is guarded against repainting what is already there.
      if (visible || moving) {
        this.requestTick();
        return;
      }
      this.lastFrame = 0;
    }

    /**
     * Re-measure once the page has gone quiet. measureGeometry forces layout, so it must
     * not land on a frame being scrolled through — and coming to rest isn't proof of that,
     * since a slow drag is a run of tiny scrolls the track closes inside a frame. So it
     * waits out a stretch with no motion and checks again before measuring.
     */
    scheduleMeasure() {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => {
        this.settleTimer = 0;
        if (!this.moving && !this.locked) this.measureGeometry();
      }, SETTLE_MS);
    }

    /**
     * Whether the section is close enough to the screen to be worth painting, with a screen
     * of margin either side so it is correct before any of it can be seen. Costs no layout.
     * @param {number} scrollY Offset the page is at.
     * @returns {boolean} True when the story should paint this frame.
     */
    inView(scrollY) {
      const screen = window.innerHeight;
      return scrollY + screen * 2 > this.sectionTop
        && scrollY - screen < this.sectionTop + this.sectionHeight;
    }

    /** Timeline position the current scroll offset maps to. */
    targetPosition(scrollY) {
      return this.progress(scrollY) * this.maxPos();
    }

    /** Re-measure and repaint (resize, unlock). Skipped while locked, since sizes lie then. */
    refresh() {
      // The fonts promise can't be unsubscribed from, so it can resolve onto a section the
      // editor has already removed.
      if (this.locked || !this.root.isConnected) return;
      this.desktop = desktopMq.matches;
      const vertical = !this.desktop;
      if (vertical !== this.vertical) {
        this.vertical = vertical;
        this.bindMode();
      }
      this.recalcTimeline();
      if (this.vertical) {
        this.measureGeometry();
        this.paintVertical();
        this.syncVideos();
        return;
      }
      this.measureStage();
      this.setPinHeight();
      this.update();
    }

    /**
     * Popups lock scrolling by pinning the body, and their own panel must stay scrollable,
     * so stand down while one is open and re-measure after. Compared against the signature
     * taken at init, so a theme that always hides overflow isn't mistaken for a lock.
     */
    watchScrollLock() {
      this.baseSignature = this.lockSignature();
      this.lockObserver = new MutationObserver(() => {
        this.scheduleRemeasure();
        const locked = this.lockSignature() !== this.baseSignature;
        if (locked === this.locked) return;
        this.locked = locked;
        this.holdStage(locked);
        if (!locked) this.refresh();
      });
      // data-scroll-locked is how this theme's own drawers and modals lock the page.
      const attributeFilter = ['class', 'style', 'data-scroll-locked'];
      this.lockObserver.observe(document.documentElement, { attributes: true, attributeFilter });
      // childList too: apps drop bars straight into the body, and a bar above the story
      // changes how much of the stage fits on screen.
      this.lockObserver.observe(document.body, { attributes: true, attributeFilter, childList: true });
    }

    /**
     * Re-measure now and again once the page has settled. Popups animate in and apps inject
     * markup in stages, so the boxes that matter land a frame or two after the mutation.
     */
    scheduleRemeasure() {
      // Coalesced: this answers every mutation on the page, and a busy storefront produces
      // a stream of them. Each pass forces a layout.
      if (this.remeasuring) return;
      this.remeasuring = true;
      let held = 0;
      const run = (last) => {
        // Held off while the track is gliding: a pass forces a layout and writes a new
        // section height, which inside a glide is felt as a stumble.
        if (this.moving && held < REMEASURE_HOLDS) {
          held++;
          window.setTimeout(() => run(last), REMEASURE_HOLD_MS);
          return;
        }
        if (last) this.remeasuring = false;
        if (this.locked) return;
        this.measureStage();
        this.setPinHeight();
      };
      requestAnimationFrame(() => run(false));
      window.setTimeout(() => run(false), 150);
      window.setTimeout(() => run(true), 400);
    }

    /**
     * Hold the stage on screen while a popup owns the page: pinning the body leaves it
     * nothing to be sticky against, so it drops to the top of a section several screens tall
     * and the visitor sees bare background. Only when it was pinned to begin with — at
     * either end of the section it sits in the flow, and fixing it there would be the bug.
     * @param {boolean} locked Whether a popup currently owns the page.
     * @returns {void}
     */
    holdStage(locked) {
      this.root.classList.toggle('hs--hold', locked && this.pinned && !this.vertical);
    }

    /** The page-level scroll properties a popup or drawer typically overrides. */
    lockSignature() {
      const body = window.getComputedStyle(document.body);
      const html = window.getComputedStyle(document.documentElement);
      return [body.position, body.overflowX, body.overflowY, body.top, body.transform,
        html.position, html.overflowX, html.overflowY].join('|');
    }

    /** Handle play/pause, mute, and carousel thumb clicks. */
    onClick(event) {
      const playBtn = event.target.closest('[data-hs-play]');
      if (playBtn) {
        const slide = playBtn.closest('[data-hs-slide]');
        const videos = slide && slide.dataset.hsType === 'carousel' && this.desktop
          ? this.carouselVideos(slide).filter((_, i) => i === this.videoLook)
          : [visibleVideo(slide)].filter(Boolean);
        if (!videos.length) return;
        const play = videos[0].paused;
        for (const video of videos) {
          if (play) this.playVideo(slide, video);
          else video.pause();
        }
        this.syncControls(slide, videos[0]);
        return;
      }

      const muteBtn = event.target.closest('[data-hs-mute]');
      if (muteBtn) {
        this.soundOn = !this.soundOn;
        this.root.querySelectorAll('video').forEach((video) => {
          video.muted = !this.soundOn;
        });
        const slide = muteBtn.closest('[data-hs-slide]');
        this.syncControls(slide, visibleVideo(slide));
        return;
      }

      const thumb = event.target.closest('[data-hs-thumb]');
      if (thumb) {
        this.setCarouselIndex(thumb.closest('[data-hs-slide]'), Number(thumb.dataset.hsThumb), true);
        if (this.vertical) this.stopCarouselAuto();
      }
    }

    /** Snap straight to the scroll position (load, resize). */
    update() {
      if (this.vertical) {
        this.paintVertical();
        this.syncVerticalActive();
        return;
      }
      if (pageScroll.holdUntil < performance.now() && !pageScroll.pending && !pageScroll.moving) {
        pageScroll.sync();
      }
      this.rendered = this.targetPosition();
      this.targetPos = this.rendered;
      this.render(this.rendered);
      // render() only hands playback over on a change of active slide, so on load the slide
      // already on screen would sit on its poster until you scrolled off it.
      this.syncVideos();
    }

    /**
     * Mobile: no timeline or track travel. Entrances are one-shot CSS; this only clears
     * the desktop track and keeps the campaign grid in document flow.
     */
    paintVertical() {
      if (this.track) this.track.style.transform = '';
      // Forgotten as well as cleared, or returning to desktop would match a shift no longer
      // on the element and skip the first paint.
      this.trackShift = null;
      this.root.style.height = '';
      this.root.style.setProperty('--hs-copy', '1');
      this.setHeaderHidden(false);
      this.revealCampaignGrid();
      this.warmGridImages();
    }

    /** Paint one frame: move the track, drive slide animations, header, and videos. */
    render(position) {
      if (this.vertical) {
        this.paintVertical();
        return;
      }
      const count = this.slides.length;
      if (!count) return;

      // One beat to a slide and a screen of travel per beat, so the timeline position *is*
      // the track's X — a straight line, with no scroll spent on anything that doesn't move
      // it. Parking the track between slides put half the section's scroll into beats where
      // nothing moved sideways; a constant speed is what reads as even.
      const x = position;
      // Guarded like the custom properties. The track is five screens wide and holds every
      // video on its own compositor layer, so it isn't restyled on a frame where it would
      // land where it already sits — which is every frame spent above or below the story.
      const shift = (-x * 100).toFixed(4);
      if (shift !== this.trackShift) {
        this.trackShift = shift;
        this.track.style.transform = `translate3d(${shift}vw, 0, 0)`;
      }

      // Entrances arm off where the gesture is headed, not where the page has eased to: the
      // drawn position trails by the length of the glide, so waiting on it meant a slide's
      // copy only started arriving once its edge was already on screen.
      const armX = this.targetPos;

      const nextIndex = clamp(Math.round(x), 0, count - 1);
      for (let index = 0; index < count; index++) {
        const slide = this.slides[index];
        const frac = clamp(x - index, 0, 1);
        const enter = clamp(x - index + 1, 0, 1);
        const armEnter = clamp(armX - index + 1, 0, 1);
        // Signed distance from the middle of this slide's time on screen, for parallax to
        // follow.
        const span = this.spans[index];
        const drive = span ? clamp(position - span.centre, -span.drift, span.drift) : 0;
        // Guarded rather than toggled: `toggle` with a force argument still counts as a
        // write, and this runs on every slide every frame.
        const active = index === nextIndex;
        if (active !== slide.hsActive) {
          slide.hsActive = active;
          slide.classList.toggle('is-active', active);
        }
        setVar(slide, '--hs-enter', enter);
        this.applySlideAnimations(slide, frac, enter, armEnter, drive);
      }

      this.setHeaderHidden(x >= 0.08);

      if (nextIndex !== this.activeIndex) {
        this.activeIndex = nextIndex;
      }
      this.handoffVideos();
    }

    /** Toggle body.hs-header-hidden when the second slide starts entering. */
    setHeaderHidden(hide) {
      if (this.headerHidden === hide) return;
      this.headerHidden = hide;
      document.body.classList.toggle('hs-header-hidden', hide);
    }

    /**
     * Per-slide work for one frame. Entrances are timed rather than scrubbed, so this mostly
     * arms sequences and starts videos; only parallax and the campaign copy are painted from
     * the scroll position every frame.
     */
    applySlideAnimations(slide, frac, enter, armEnter, drive) {
      const type = slide.dataset.hsType;

      // Any slide that marks up layers gets depth, whatever its type. No-op for the rest.
      this.paintParallax(slide, drive);

      if (type === 'carousel') {
        if (armEnter > 0) this.warmCarouselLooks(slide);
        this.armEntrance(slide, enter, frac);
        armMark(slide, 'is-look-2', enter, frac, LOOK_2_IN);
        armMark(slide, 'is-look-3', enter, frac, LOOK_3_IN);
        return;
      }

      if (type === 'campaign') {
        setVar(slide, '--hs-copy', smooth(clamp((enter - 0.35) / 0.35, 0, 1)));
        if (frac <= LEAVING && this.gridColumnVisible(slide, 0) >= VIDEO_IN) {
          slide.dataset.hsRevealed = 'true';
          this.revealCampaignGrid();
        } else if (enter <= 0.001 && slide.dataset.hsRevealed === 'true') {
          delete slide.dataset.hsRevealed;
          this.replayCampaignVideo(slide);
        }
        this.armGridColumn(slide, 'is-grid-col-2', 1, frac, enter);
        this.armGridColumn(slide, 'is-grid-col-3', 2, frac, enter);
        return;
      }

      if (type === 'split') {
        this.armEntrance(slide, enter, frac);
        return;
      }

      if (type === 'statement') {
        // Revamp: how far through its time on screen the slide is, for the image to pan by.
        // Reduced motion holds it centred, as the parallax does.
        setVar(slide, '--hs-drive', reduceMotion ? 0 : drive);
        this.armEntrance(slide, enter, frac);
      }
    }

    /**
     * Arm a slide's copy entrance on arrival, disarm it once gone so a return trip replays.
     * The sequences run on their own clock from this class rather than being scrubbed off
     * the wheel, which made them as choppy as the scroll events behind them.
     * @param {HTMLElement} slide Slide to arm.
     * @param {number} enter 0–1 how far on screen the slide has come.
     * @param {number} frac 0–1 how far it has gone back off the other side.
     * @returns {void}
     */
    armEntrance(slide, enter, frac) {
      // `frac` climbs as the slide leaves, so this covers the way in and stops short of the
      // halfway mark, where the next slide takes over.
      const inFrame = slide.classList.contains('is-in-frame');
      if (!inFrame && frac <= LEAVING && enter >= IN_FRAME) slide.classList.add('is-in-frame');
      else if (inFrame && enter <= 0.001) slide.classList.remove('is-in-frame');
    }

    /**
     * The breakpoint-correct video for each look in a carousel slide. Cached, since
     * `handoffVideos` asks every frame the slide is arming and the walk searches the
     * slide and each panel to return the same three elements. `bindMode` drops it, as which
     * video is right depends on the breakpoint.
     * @param {HTMLElement} slide Carousel slide to read.
     * @returns {HTMLVideoElement[]} One video per look, in document order.
     */
    carouselVideos(slide) {
      if (!slide.hsVideos) {
        slide.hsVideos = Array.from(slide.querySelectorAll('[data-hs-carousel-item]'))
          .map((panel) => visibleVideo(panel))
          .filter(Boolean);
      }
      return slide.hsVideos;
    }

    /**
     * Buffer a look without leaving it playing. play() once so the file starts,
     * then park it so only the live look is moving.
     */
    primeLook(video) {
      if (!video || video.hsPrimed) return;
      video.hsPrimed = true;
      video.preload = 'auto';
      this.muteForAutoplay(video);
      if (video.readyState >= 3) return;
      const park = () => {
        if (video.hsPaused !== false) video.pause();
      };
      const play = video.play();
      if (play && play.then) {
        play.then(() => {
          if (video.readyState >= 2) park();
          else video.addEventListener('canplay', park, { once: true });
        }).catch(park);
      }
    }

    /** Buffer every ICONS look on load; they stay paused until they are the live one. */
    warmCarouselLooks(slide) {
      this.carouselVideos(slide).forEach((video) => this.primeLook(video));
    }

    /** 0–1 of an element's box that sits in the viewport. */
    boxVisible(el) {
      if (!el) return 0;
      const box = el.getBoundingClientRect();
      const area = box.width * box.height;
      if (area <= 0) return 0;
      const visW = Math.min(box.right, window.innerWidth) - Math.max(box.left, 0);
      const visH = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
      if (visW <= 0 || visH <= 0) return 0;
      return clamp((visW * visH) / area, 0, 1);
    }

    /** Breakpoint-correct videos on a slide, in order. ICONS is one per look. */
    playableVideos(slide) {
      if (!slide) return [];
      if (slide.dataset.hsType === 'carousel' && this.desktop) return this.carouselVideos(slide);
      const videos = [];
      slide.querySelectorAll('video').forEach((video) => {
        if (this.desktop && video.classList.contains('hs-video--mobile')) return;
        if (!this.desktop && video.classList.contains('hs-video--desktop')) return;
        videos.push(video);
      });
      return videos;
    }

    /**
     * Rightmost part that is 70% on screen — every slide, not only ICONS.
     * A part is each look video, or another slide once its video or the slide itself is in.
     */
    latestVisibleVideo() {
      let slideIndex = this.videoIndex;
      let look = this.videoLook < 0 ? 0 : this.videoLook;
      this.slides.forEach((slide, i) => {
        const videos = this.playableVideos(slide);
        const carousel = slide.dataset.hsType === 'carousel' && this.desktop;
        if (carousel) {
          if (this.boxVisible(videos[0]) < 0.25 && this.boxVisible(slide) < 0.25) return;
          slideIndex = i;
          look = 0;
          if (slide.classList.contains('is-look-3')) look = 2;
          else if (slide.classList.contains('is-look-2')) look = 1;
          return;
        }
        const videoIn = videos.some((video) => this.boxVisible(video) >= VIDEO_IN);
        if (!videoIn && this.boxVisible(slide) < VIDEO_IN) return;
        slideIndex = i;
        look = 0;
      });
      return { slideIndex, look };
    }

    /** Stop the playing video and start the latest one once that video is 70% visible. */
    handoffVideos() {
      const next = this.latestVisibleVideo();
      if (next.slideIndex === this.videoIndex && next.look === this.videoLook) return;
      this.videoIndex = next.slideIndex;
      this.videoLook = next.look;
      this.syncVideos();
    }

    /**
     * Drift each look sideways at its own rate, so the three read at different depths.
     * Written to the images rather than through a custom property on the slide, which would
     * invalidate styles for everything inside it, videos and copy included.
     * @param {HTMLElement} slide Carousel slide holding the looks to drift.
     * @param {number} drive Signed distance from the middle of the slide's time on screen.
     * @returns {void}
     */
    paintParallax(slide, drive) {
      if (reduceMotion) return;
      if (!slide.hsParallax) {
        slide.hsParallax = Array.from(slide.querySelectorAll('[data-hs-parallax]'), (panel) => ({
          media: panel.querySelector('.hs-media'),
          // Clamped to the room the panel gives its image, so a rate typed into the markup
          // can't walk a layer far enough to show the panel behind it.
          rate: clamp(parseFloat(panel.dataset.hsParallax) || 0, -PARALLAX_ROOM, PARALLAX_ROOM),
        })).filter((layer) => layer.media && layer.rate);
      }
      for (const layer of slide.hsParallax) {
        const shift = (drive * layer.rate).toFixed(3);
        if (layer.media.hsShift === shift) continue;
        layer.media.hsShift = shift;
        layer.media.style.transform = `translate3d(${shift}vw, 0, 0)`;
      }
    }

    /**
     * Drop any drift left on the looks when the layout switches to the stacked one.
     * @returns {void}
     */
    clearParallax() {
      for (const slide of this.slides) {
        if (!slide.hsParallax) continue;
        for (const layer of slide.hsParallax) {
          layer.media.hsShift = null;
          layer.media.style.transform = '';
        }
      }
    }

    /** Mute a video in the way Safari's autoplay check actually reads. */
    muteForAutoplay(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
    }

    /**
     * Start a video. Safari often refuses the first play() — file not ready, Low Power
     * Mode, or sound on — so this waits for canplay and retries muted.
     */
    playVideo(slide, video) {
      if (!video) return;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      if (!this.soundOn) this.muteForAutoplay(video);

      const token = (video.hsPlayToken || 0) + 1;
      video.hsPlayToken = token;
      video.hsPaused = false;

      const wanted = () => video.hsPlayToken === token && !video.hsPaused;

      const afterPlay = () => {
        if (!wanted()) return;
        if (this.soundOn) {
          video.muted = false;
          if (video.muted) this.soundOn = false;
        }
        this.syncControls(slide, video);
      };

      const attempt = () => {
        if (!wanted() || !video.paused) return;
        const play = video.play();
        if (!play || !play.catch) return;
        play.then(afterPlay).catch(() => {
          if (!wanted()) return;
          this.muteForAutoplay(video);
          this.soundOn = false;
          video.play().then(afterPlay).catch(() => {
            if (!wanted()) return;
            if (video.readyState < 2) {
              video.preload = 'auto';
              video.addEventListener('canplay', attempt, { once: true });
              video.addEventListener('loadeddata', attempt, { once: true });
            }
          });
        });
      };

      video.preload = 'auto';
      if (video.readyState < 2) {
        video.addEventListener('canplay', attempt, { once: true });
        video.addEventListener('loadeddata', attempt, { once: true });
      }
      attempt();
    }

    /** Activate a carousel panel (and matching thumb). Locked = user chose via thumb. */
    setCarouselIndex(slide, index, locked, panels) {
      if (!slide) return;
      const list = panels || slide.querySelectorAll('[data-hs-carousel-item]');
      if (!list.length) return;
      const next = clamp(index, 0, list.length - 1);
      if (slide.dataset.hsCarouselIndex === String(next) && !locked) return;
      const changed = slide.dataset.hsCarouselIndex !== String(next);
      slide.dataset.hsCarouselIndex = String(next);
      list.forEach((panel, i) => panel.classList.toggle('is-active', i === next));
      slide.querySelectorAll('[data-hs-thumb]').forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === next);
      });
      if (locked) slide.dataset.hsCarouselLocked = 'true';
      if (changed) {
        // Each visit to a video look has to play out again before autoplay moves on.
        const video = visibleVideo(list[next]);
        if (video) {
          video.hsLookDone = false;
          video.hsLastT = video.currentTime;
        }
      }
      if (changed && this.slides[this.activeIndex] === slide) this.syncVideos();
    }

    /** Play the live video; pause every other one. */
    syncVideos() {
      this.slides.forEach((slide, index) => {
        const isLive = index === (this.vertical ? this.videoIndex : this.videoIndex);
        const carousel = slide.dataset.hsType === 'carousel';
        let targets;
        const looks = carousel && this.desktop ? this.carouselVideos(slide) : [];
        if (isLive && carousel && this.desktop) {
          const current = looks[this.videoLook] || looks[0];
          targets = current ? [current] : [];
        } else if (isLive && carousel && !this.vertical) {
          targets = this.carouselVideos(slide);
        } else {
          const scope = carousel
            ? slide.querySelector('[data-hs-carousel-item].is-active') || slide
            : slide;
          const target = visibleVideo(scope);
          targets = target ? [target] : [];
        }
        if (slide === this.campaign && Math.abs(index - this.videoIndex) <= 1) this.warmGridImages();
        slide.querySelectorAll('video').forEach((video) => {
          if (!this.soundOn) this.muteForAutoplay(video);
          const wanted = isLive && targets.indexOf(video) !== -1;
          if (wanted && Math.abs(index - this.videoIndex) <= 1) video.preload = 'auto';
          if (wanted) {
            this.playVideo(slide, video);
          } else {
            if (looks.indexOf(video) !== -1) this.primeLook(video);
            video.hsPaused = true;
            video.hsPlayToken = (video.hsPlayToken || 0) + 1;
            video.pause();
          }
        });
        if (isLive) this.syncControls(slide, targets[0]);
      });
    }

    /** Sync play/pause and mute button icons to the current video state. */
    syncControls(slide, video) {
      if (!slide) return;
      slide.querySelectorAll('[data-hs-play]').forEach((button) => {
        const paused = !video || video.paused;
        const playIcon = button.querySelector('[data-icon-play]');
        const pauseIcon = button.querySelector('[data-icon-pause]');
        if (playIcon) playIcon.hidden = !paused;
        if (pauseIcon) pauseIcon.hidden = paused;
      });
      slide.querySelectorAll('[data-hs-mute]').forEach((button) => {
        const muted = !video || video.muted || !this.soundOn;
        const volumeIcon = button.querySelector('[data-icon-volume]');
        const muteIcon = button.querySelector('[data-icon-mute]');
        if (volumeIcon) volumeIcon.hidden = muted;
        if (muteIcon) muteIcon.hidden = !muted;
        button.setAttribute('aria-pressed', muted ? 'true' : 'false');
      });
    }

    /** Theme editor: bring the slide owning the selected block on screen. */
    goToBlock(node) {
      const slide = node.closest('[data-hs-slide]');
      const index = slide ? this.slides.indexOf(slide) : -1;
      if (index < 0) return;
      if (this.vertical) {
        slide.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      // One beat to a slide, so the index is the position that centres it.
      const position = index;
      const total = this.maxPos();
      const range = this.sectionHeight - this.stageHeight;
      const top = this.sectionTop + range * (total <= 0 ? 0 : clamp(position / total, 0, 1));
      // Straight to the offset rather than eased, so picking a block in the editor lands on
      // it instead of gliding there.
      window.scrollTo({ top, left: 0, behavior: 'instant' });
      // Told about it, or the driver would ease the page straight back again.
      pageScroll.sync();
      this.rendered = this.targetPosition(top);
      this.requestTick();
    }
  }

  /** Create a HorizontalStory instance for one section root (idempotent). */
  function init(container) {
    if (!container || container._horizontalStory) return;
    container._horizontalStory = new HorizontalStory(container);
  }

  /** Init every [data-horizontal-story] on the page. */
  function initAll() {
    document.querySelectorAll('[data-horizontal-story]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-horizontal-story]'));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const el = event.target.querySelector('[data-horizontal-story]');
    if (el && el._horizontalStory) {
      el._horizontalStory.destroy();
      el._horizontalStory = null;
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const story = event.target.closest('[data-horizontal-story]');
    if (story && story._horizontalStory) story._horizontalStory.goToBlock(event.target);
  });
})();
